/*ReactJS*/
import React, { useEffect, useState } from 'react';

/*Service*/
import { FieldMask } from 'google-protobuf/google/protobuf/field_mask_pb';
import {
    AdditionalDeliverables,
    CreateSecurityMasterRequest,
    SecurityMaster,
    UpdateSecurityMasterRequest,
} from '../../../proto/assetpb/securitymaster_grpc_web_pb';

/*Toast Notification*/
import {
    notifyError,
    notifySuccess,
} from '../../../components/Notification/Notification';

/*Material UI Components*/
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Backdrop,
    Box,
    Button,
    Divider,
    Fade,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    TextField,
    Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Autocomplete from '@material-ui/lab/Autocomplete';

import useStyles from '../../../styles';
import {
    ASSET_CATEGORY_OPTIONS,
    ASSET_TYPE_OPTIONS,
    EQUITY_MULTIPLIER_OPTIONS,
    getAssetTypeDefaults,
    LEVERAGE_FACTOR_OPTIONS,
    SEC_SUB_TYPE_OPTIONS,
    SECURITY_TYPE_OPTIONS,
} from './constants';

const google_date = require('../../../google/type/date_pb.js');

const IDENTITY_FIELDS = [
    { name: 'symbol', label: 'Symbol', required: true },
    { name: 'symbolDescription', label: 'Symbol Description' },
    { name: 'cusip', label: 'Cusip', required: true },
    { name: 'originalCusip', label: 'Original Cusip', required: true },
    {
        name: 'assetType',
        label: 'Asset Type',
        required: true,
        type: 'assetType',
    },
];

const CLASSIFICATION_FIELDS = [
    {
        name: 'securityType',
        label: 'Security Type',
        type: 'creatableSelect',
        options: SECURITY_TYPE_OPTIONS,
    },
    {
        name: 'secSubType',
        label: 'Sec Sub Type',
        type: 'creatableSelect',
        options: SEC_SUB_TYPE_OPTIONS,
    },
    { name: 'isin', label: 'ISIN' },
];

const SHARED_PRIMARY_FIELDS = [
    { name: 'exchangeMic', label: 'Exchange MIC' },
    { name: 'startDate', label: 'Start Date', type: 'date', required: true },
    { name: 'endDate', label: 'End Date', type: 'date', required: true },
    { name: 'currency', label: 'Currency' },
    { name: 'marginable', label: 'Marginable', type: 'yesNo' },
    {
        name: 'leverageFactor',
        label: 'Leverage Factor',
        type: 'select',
        options: LEVERAGE_FACTOR_OPTIONS,
    },
    {
        name: 'multiplier',
        label: 'Multiplier',
        type: 'multiplier',
        required: true,
    },
];

const EXCHANGE_COUNTRY_FIELD = {
    name: 'exchangeCountry',
    label: 'Exchange Country',
};

const OPTION_PRIMARY_FIELDS = [
    { name: 'underlyingSymbol', label: 'Underlying Symbol' },
    { name: 'size', label: 'Size', type: 'int' },
];

const MORE_FIELDS = [
    { name: 'whenIssued', label: 'When Issued', type: 'yesNo' },
    { name: 'dtcEligible', label: 'DTC Eligible', type: 'yesNo' },
    { name: 'nsccEligibility', label: 'NSCC Eligibility', type: 'yesNo' },
    { name: 'taxStatus', label: 'Tax Status', type: 'yesNo' },
    { name: 'exchangeCode', label: 'Exchange Code' },
    { name: 'sicCode', label: 'SIC Code' },
    { name: 'country', label: 'Country' },
    {
        name: 'assetCategory',
        label: 'Asset Category',
        type: 'creatableSelect',
        options: ASSET_CATEGORY_OPTIONS,
    },
    { name: 'issuerTypeCode', label: 'Issuer Type Code' },
    { name: 'externalId', label: 'External ID' },
    { name: 'fileDate', label: 'File Date', type: 'date' },
    { name: 'inactiveDate', label: 'Inactive Date', type: 'date' },
    { name: 'dataOrigin', label: 'Data Origin' },
];

const EQUITY_MORE_FIELDS = [
    { name: 'underlyingSymbol', label: 'Underlying Symbol' },
    { name: 'size', label: 'Size', type: 'int' },
];

const ADDITIONAL_DELIVERABLES_FIELDS = [
    { name: 'adSymbol', label: 'Symbol' },
    { name: 'adAmount', label: 'Amount' },
    {
        name: 'adAllocationPercentage',
        label: 'Allocation Percentage',
    },
    { name: 'adSettlementType', label: 'Settlement Type' },
];

const UPDATE_FIELDS = [
    { name: 'startDate', label: 'Start Date', type: 'date', required: true },
    { name: 'endDate', label: 'End Date', type: 'date', required: true },
];

const EMPTY_ADD_FORM = {
    symbol: '',
    cusip: '',
    originalCusip: '',
    assetType: 'E',
    symbolDescription: '',
    startDate: '',
    endDate: '2050-12-31',
    securityType: '',
    secSubType: '',
    marginable: 'N',
    whenIssued: 'N',
    dtcEligible: 'N',
    nsccEligibility: 'N',
    taxStatus: 'N',
    exchangeCode: '',
    sicCode: '',
    exchangeMic: '',
    exchangeCountry: 'US',
    leverageFactor: '1',
    size: '',
    multiplier: '1',
    country: '',
    currency: 'USD',
    assetCategory: '',
    issuerTypeCode: '',
    underlyingSymbol: '',
    isin: '',
    externalId: '',
    fileDate: '',
    inactiveDate: '',
    dataOrigin: '',
    adSymbol: '',
    adAmount: '',
    adAllocationPercentage: '',
    adSettlementType: '',
};

function getEmptyAddForm(assetType = 'E') {
    return {
        ...EMPTY_ADD_FORM,
        assetType,
        ...getAssetTypeDefaults(assetType),
    };
}

function getPrimaryFields(form) {
    if (form.assetType === 'E') {
        return [
            ...SHARED_PRIMARY_FIELDS.slice(0, 4),
            ...CLASSIFICATION_FIELDS,
            EXCHANGE_COUNTRY_FIELD,
            ...SHARED_PRIMARY_FIELDS.slice(4),
        ];
    }
    if (form.assetType === 'O') {
        return [...SHARED_PRIMARY_FIELDS, ...OPTION_PRIMARY_FIELDS];
    }
    return SHARED_PRIMARY_FIELDS;
}

function getMoreFields(form) {
    if (form.assetType === 'E') {
        return [...EQUITY_MORE_FIELDS, ...MORE_FIELDS];
    }
    if (form.assetType === 'O') {
        return [
            ...CLASSIFICATION_FIELDS,
            EXCHANGE_COUNTRY_FIELD,
            ...MORE_FIELDS,
        ];
    }
    return MORE_FIELDS;
}

function getAddFormFields(form) {
    return [
        ...IDENTITY_FIELDS,
        ...getPrimaryFields(form),
        ...getMoreFields(form),
    ];
}

function toGoogleDate(dateStr) {
    const gglDate = new google_date.Date();
    const [y, m, d] = dateStr.split('-').map(Number);
    gglDate.setYear(y);
    gglDate.setMonth(m);
    gglDate.setDay(d);
    return gglDate;
}

function setStringField(req, setter, value) {
    if (value !== undefined && value !== null && value !== '') {
        setter.call(req, value);
    }
}

function buildSecurityMasterRequest(form, mode) {
    const req = new SecurityMaster();

    if (mode === 'edit') {
        req.setSecurityId(form.securityId);
        if (form.startDate) {
            req.setStartDate(toGoogleDate(form.startDate));
        }
        if (form.endDate) {
            req.setEndDate(toGoogleDate(form.endDate));
        }
        return req;
    }

    req.setSymbol(form.symbol);
    req.setCusip(form.cusip);
    req.setOriginalCusip(form.originalCusip);
    req.setAssetType(form.assetType);

    setStringField(req, req.setSymbolDescription, form.symbolDescription);
    setStringField(req, req.setSecurityType, form.securityType);
    setStringField(req, req.setSecSubType, form.secSubType);
    setStringField(req, req.setExchangeCode, form.exchangeCode);
    setStringField(req, req.setSicCode, form.sicCode);
    setStringField(req, req.setExchangeMic, form.exchangeMic);
    setStringField(req, req.setExchangeCountry, form.exchangeCountry);
    setStringField(req, req.setSize, form.size);
    setStringField(req, req.setMultiplier, form.multiplier);
    setStringField(req, req.setCountry, form.country);
    setStringField(req, req.setCurrency, form.currency);
    setStringField(req, req.setAssetCategory, form.assetCategory);
    setStringField(req, req.setIssuerTypeCode, form.issuerTypeCode);
    setStringField(req, req.setUnderlyingSymbol, form.underlyingSymbol);
    setStringField(req, req.setIsin, form.isin);
    setStringField(req, req.setExternalId, form.externalId);
    setStringField(req, req.setDataOrigin, form.dataOrigin);

    if (form.marginable) {
        req.setMarginable(form.marginable);
    }
    if (form.whenIssued) {
        req.setWhenIssued(form.whenIssued);
    }
    if (form.nsccEligibility) {
        req.setNsccEligibility(form.nsccEligibility);
    }
    if (form.taxStatus) {
        req.setTaxStatus(form.taxStatus);
    }
    if (form.dtcEligible === 'Y') {
        req.setDtcEligible(true);
    }
    if (form.leverageFactor !== '') {
        req.setLeverageFactor(parseInt(form.leverageFactor, 10) || 0);
    }
    if (form.startDate) {
        req.setStartDate(toGoogleDate(form.startDate));
    }
    if (form.endDate) {
        req.setEndDate(toGoogleDate(form.endDate));
    }
    if (form.fileDate) {
        req.setFileDate(toGoogleDate(form.fileDate));
    }
    if (form.inactiveDate) {
        req.setInactiveDate(toGoogleDate(form.inactiveDate));
    }

    const hasAdditionalDeliverables =
        form.adSymbol ||
        form.adAmount ||
        form.adAllocationPercentage ||
        form.adSettlementType;
    if (hasAdditionalDeliverables) {
        const ad = new AdditionalDeliverables();
        setStringField(ad, ad.setSymbol, form.adSymbol);
        setStringField(ad, ad.setAmount, form.adAmount);
        setStringField(ad, ad.setAllocationPercentage, form.adAllocationPercentage);
        setStringField(ad, ad.setSettlementType, form.adSettlementType);
        req.setAdditionalDeliverables(ad);
    }

    return req;
}

function validateForm(form, mode) {
    if (mode === 'add') {
        for (const field of getAddFormFields(form)) {
            if (field.required && !form[field.name]) {
                notifyError(field.label + ' is a required field.');
                return false;
            }
        }
    }

    if (mode === 'edit') {
        if (!form.securityId) {
            notifyError('Security ID is required.');
            return false;
        }
        if (!form.startDate || !form.endDate) {
            notifyError('Start Date and End Date are required.');
            return false;
        }
    }

    if (form.startDate && form.endDate && form.endDate < form.startDate) {
        notifyError('End Date must be on or after Start Date.');
        return false;
    }

    return true;
}

function updateFormField(form, setForm, name, value) {
    const next = { ...form, [name]: value };
    if (name === 'cusip' && (!form.originalCusip || form.originalCusip === form.cusip)) {
        next.originalCusip = value;
    }
    if (name === 'assetType') {
        Object.assign(next, getAssetTypeDefaults(value));
    }
    setForm(next);
}

function renderFieldLabel(field) {
    if (!field.required) {
        return field.label;
    }
    return (
        <>
            {field.label}
            <Box
                component="span"
                style={{
                    color: '#d32f2f',
                    fontSize: '1.25rem',
                    fontWeight: 700,
                    lineHeight: 1,
                    marginLeft: 2,
                    verticalAlign: 'middle',
                }}
            >
                *
            </Box>
        </>
    );
}

function renderCreatableSelect(field, value, label, disabled, onChange) {
    const options = field.options || [];
    return (
        <Box key={field.name} mb={2}>
            <Autocomplete
                freeSolo
                fullWidth
                disabled={disabled}
                options={options}
                value={value}
                onChange={(_event, newValue) => onChange(newValue ?? '')}
                onInputChange={(_event, newInputValue, reason) => {
                    if (reason === 'input') {
                        onChange(newInputValue);
                    }
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        margin="normal"
                        InputLabelProps={{ shrink: true }}
                    />
                )}
            />
        </Box>
    );
}

function renderFieldRow(field, form, setForm, readOnly) {
    const value = form[field.name] ?? '';
    const disabled = readOnly || field.readOnly;
    const label = renderFieldLabel(field);
    const onChange = (nextValue) => updateFormField(form, setForm, field.name, nextValue);

    if (field.type === 'assetType') {
        return (
            <Box key={field.name} mb={2}>
                <InputLabel shrink>{label}</InputLabel>
                <Select
                    fullWidth
                    value={value}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.value)}
                >
                    {ASSET_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </Box>
        );
    }

    if (field.type === 'yesNo') {
        return (
            <Box key={field.name} mb={2}>
                <InputLabel shrink>{label}</InputLabel>
                <Select
                    fullWidth
                    value={value || 'N'}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.value)}
                >
                    <MenuItem value="Y">Yes</MenuItem>
                    <MenuItem value="N">No</MenuItem>
                </Select>
            </Box>
        );
    }

    if (field.type === 'creatableSelect') {
        return renderCreatableSelect(field, value, label, disabled, onChange);
    }

    if (field.type === 'select') {
        return (
            <Box key={field.name} mb={2}>
                <InputLabel shrink>{label}</InputLabel>
                <Select
                    fullWidth
                    value={value}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.value)}
                >
                    {(field.options || []).map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            </Box>
        );
    }

    if (field.type === 'multiplier') {
        if (form.assetType === 'O') {
            return (
                <TextField
                    key={field.name}
                    fullWidth
                    label={label}
                    type="number"
                    value={value}
                    disabled={disabled}
                    margin="normal"
                    onChange={(e) => onChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 1, min: 0 }}
                />
            );
        }
        return (
            <Box key={field.name} mb={2}>
                <InputLabel shrink>{label}</InputLabel>
                <Select
                    fullWidth
                    value={value}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.value)}
                >
                    {EQUITY_MULTIPLIER_OPTIONS.map((option) => (
                        <MenuItem key={option || 'empty'} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            </Box>
        );
    }

    if (field.type === 'date') {
        return (
            <TextField
                key={field.name}
                fullWidth
                label={label}
                type="date"
                value={value}
                disabled={disabled}
                margin="normal"
                onChange={(e) => onChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
            />
        );
    }

    if (field.type === 'int') {
        return (
            <TextField
                key={field.name}
                fullWidth
                label={label}
                type="number"
                value={value}
                disabled={disabled}
                margin="normal"
                onChange={(e) => onChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 1 }}
            />
        );
    }

    return (
        <TextField
            key={field.name}
            fullWidth
            label={label}
            value={value}
            disabled={disabled}
            margin="normal"
            onChange={(e) => onChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
        />
    );
}

function renderReadOnlyField(label, value) {
    return (
        <TextField
            key={label}
            fullWidth
            label={label}
            value={value || ''}
            disabled
            margin="normal"
            InputLabelProps={{ shrink: true }}
        />
    );
}

export default function SecurityMasterFormModal({
    open,
    mode,
    initialForm,
    securityMasterClient,
    onClose,
    onSaved,
}) {
    const classes = useStyles();
    const [form, setForm] = useState(EMPTY_ADD_FORM);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            if (mode === 'add') {
                setForm({
                    ...getEmptyAddForm('E'),
                    startDate: new Date().toISOString().slice(0, 10),
                });
            } else {
                setForm(initialForm || {});
            }
        }
    }, [open, mode, initialForm]);

    const handleSave = () => {
        if (!validateForm(form, mode)) {
            return;
        }

        setSaving(true);
        const securityMaster = buildSecurityMasterRequest(form, mode);

        if (mode === 'add') {
            const req = new CreateSecurityMasterRequest();
            req.setSecurityMaster(securityMaster);
            securityMasterClient.createSecurityMaster(req, {}, (err, res) => {
                setSaving(false);
                if (err) {
                    console.error(err);
                    notifyError(err.message);
                    return;
                }
                notifySuccess('Security master record has been added.');
                onSaved(res.toObject().securityMaster);
                onClose();
            });
            return;
        }

        const req = new UpdateSecurityMasterRequest();
        req.setSecurityMaster(securityMaster);
        const updateMask = new FieldMask();
        updateMask.setPathsList(['start_date', 'end_date']);
        req.setUpdateMask(updateMask);
        securityMasterClient.updateSecurityMaster(req, {}, (err, res) => {
            setSaving(false);
            if (err) {
                console.error(err);
                notifyError(err.message);
                return;
            }
            notifySuccess('Security master record has been updated.');
            onSaved(res.toObject().securityMaster);
            onClose();
        });
    };

    const title =
        mode === 'add' ? 'Add New Security' : 'Edit Security';

    return (
        <Modal
            className={classes.modalBackdrop}
            aria-labelledby="security-master-modal-title"
            open={open}
            onClose={onClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 500 }}
        >
            <Fade in={open}>
                <div
                    className={classes.fadeModalMd}
                    style={{ maxHeight: '90vh', overflowY: 'auto' }}
                >
                    <Typography
                        id="security-master-modal-title"
                        className={classes.textBold}
                        variant="h4"
                        gutterBottom
                    >
                        {title}
                    </Typography>
                    <form
                        className={classes.modalForm}
                        noValidate
                        autoComplete="off"
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleSave();
                        }}
                    >
                        {mode === 'edit' ? (
                            <>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    gutterBottom
                                >
                                    Only Start Date and End Date can be updated.
                                </Typography>
                                {renderReadOnlyField('Security ID', form.securityId)}
                                {renderReadOnlyField('Symbol', form.symbol)}
                                {renderReadOnlyField('Cusip', form.cusip)}
                                {UPDATE_FIELDS.map((field) =>
                                    renderFieldRow(field, form, setForm, false)
                                )}
                            </>
                        ) : (
                            <>
                                {IDENTITY_FIELDS.map((field) =>
                                    renderFieldRow(field, form, setForm, false)
                                )}
                                {getPrimaryFields(form).map((field) =>
                                    renderFieldRow(field, form, setForm, false)
                                )}
                                <Divider style={{ margin: '16px 0' }} />
                                <Accordion defaultExpanded={false}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography>More fields</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails
                                        style={{ flexDirection: 'column', width: '100%' }}
                                    >
                                        {getMoreFields(form).map((field) =>
                                            renderFieldRow(field, form, setForm, false)
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                                <Accordion defaultExpanded={false}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography>Additional Deliverables</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails
                                        style={{ flexDirection: 'column', width: '100%' }}
                                    >
                                        {ADDITIONAL_DELIVERABLES_FIELDS.map((field) =>
                                            renderFieldRow(field, form, setForm, false)
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            </>
                        )}
                        <Box className={classes.modalFooter}>
                            <Button onClick={onClose} style={{ marginRight: 12 }}>
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                type="submit"
                                disabled={saving}
                            >
                                Save
                            </Button>
                        </Box>
                    </form>
                </div>
            </Fade>
        </Modal>
    );
}

export {
    ADDITIONAL_DELIVERABLES_FIELDS,
    IDENTITY_FIELDS,
    MORE_FIELDS,
    SHARED_PRIMARY_FIELDS,
    UPDATE_FIELDS,
    buildSecurityMasterRequest,
    getEmptyAddForm,
    validateForm,
};

